/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { chatService } from '../../services/chatService';

import { Spin, Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import ProfileDetail from '../../components/ProfileDetail/ProfileDetail';
import { useTranslation } from 'react-i18next';
import { messageService } from '../../services/messageService';

const Chat = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { id } = useParams();
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [messages, setMessages] = useState([]);
    const [otherUser, setOtherUser] = useState(null);
    const [showProfileDetail, setShowProfileDetail] = useState(false);
    const [isBlocked, setIsBlocked] = useState(false);
    const authState = useSelector(state => state.auth);
    const [hasMoreMessages, setHasMoreMessages] = useState(false);
    const [oldestMessageTimestamp, setOldestMessageTimestamp] = useState(null);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    // Etat de blocage (blockedBy + blockInfo) tenu à jour en temps réel via subscribeToConversation,
    // utilisé pour recalculer le filtrage des messages de façon réactive (voir displayedMessages).
    const [blockState, setBlockState] = useState({ blockedBy: [], blockInfo: {} });
    const chatBoxRef = useRef(null);
    const headerRef = useRef(null);
    const footerRef = useRef(null);
    // Hauteur reelle disponible pour la zone de messages, mesuree en JS plutot que
    // deviner un nombre fixe en CSS (calc(100vh - 250px)). Ce chiffre fixe supposait
    // une hauteur combinee header+footer constante : des qu'un des deux etait plus
    // grand que prevu (footer plus haut, header sur 2 lignes...), les derniers messages
    // se retrouvaient caches sous le footer fixe sans pouvoir y scroller. On utilise en
    // plus window.visualViewport quand disponible car window.innerHeight/100vh ne suit
    // pas la barre d'adresse mobile qui apparait/disparait au scroll.
    const [chatAreaHeight, setChatAreaHeight] = useState(null);

    useEffect(() => {
        // Tant que loading est true, le composant retourne le <Spin/> plus bas : le
        // header/footer reels ne sont pas encore dans le DOM et headerRef/footerRef
        // valent null. Un ResizeObserver branche directement sur les vrais elements
        // des qu'ils existent et se re-declenche a chaque changement de leur taille
        // (plutot que de deviner quels states React font varier cette taille).
        const updateChatAreaHeight = () => {
            const viewportHeight = window.visualViewport?.height || window.innerHeight;
            const headerHeight = headerRef.current?.offsetHeight || 0;
            const footerHeight = footerRef.current?.offsetHeight || 0;
            setChatAreaHeight(viewportHeight - headerHeight - footerHeight);
        };

        updateChatAreaHeight();

        const resizeObserver = new ResizeObserver(updateChatAreaHeight);
        if (headerRef.current) resizeObserver.observe(headerRef.current);
        if (footerRef.current) resizeObserver.observe(footerRef.current);

        window.addEventListener('resize', updateChatAreaHeight);
        window.visualViewport?.addEventListener('resize', updateChatAreaHeight);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateChatAreaHeight);
            window.visualViewport?.removeEventListener('resize', updateChatAreaHeight);
        };
    }, [loading]);

    const scrollToBottom = () => {
        if (chatBoxRef.current) {
            const scrollHeight = chatBoxRef.current.scrollHeight;
            const height = chatBoxRef.current.clientHeight;
            const maxScrollTop = scrollHeight - height;
            chatBoxRef.current.scrollTop = maxScrollTop > 0 ? maxScrollTop : 0;
        }
    };

    // Scroll to bottom when messages change
    useEffect(() => {
        if (messages.length > 0) {
            setTimeout(scrollToBottom, 100);
        }
    }, [messages]);

    // Messages effectivement affichés : si j'ai bloqué la conversation, on masque les messages
    // reçus après le blocage (sauf les miens). Recalculé automatiquement dès que blockState change,
    // plutôt que figé au moment de l'ouverture de la conversation.
    const displayedMessages = useMemo(() => {
        if (!blockState.blockedBy.includes(authState.uid)) {
            return messages;
        }

        const myBlockInfo = blockState.blockInfo?.[authState.uid];
        const blockTime = myBlockInfo?.blockedAt ? new Date(myBlockInfo.blockedAt) : null;
        if (!blockTime) {
            return messages;
        }

        return messages.filter(msg => {
            const msgTime = new Date(msg.createdAt * 1000);
            return msgTime < blockTime || msg.isUser;
        });
    }, [messages, blockState, authState.uid]);

    const loadOlderMessages = async () => {
        if (!hasMoreMessages || isLoadingMore) return;
        
        setIsLoadingMore(true);
        try {
            const olderMessages = await chatService.getOlderMessages(
                id,
                oldestMessageTimestamp,
                authState.uid,
                20
            );
            
            if (olderMessages.length < 20) {
                setHasMoreMessages(false);
            }
            
            if (olderMessages.length > 0) {
                setMessages(prev => [...olderMessages, ...prev]);
                setOldestMessageTimestamp(olderMessages[0].createdAt);
            }
        } catch (error) {
            console.error('Error loading older messages:', error);
        } finally {
            setIsLoadingMore(false);
        }
    };

    useEffect(() => {
        // unsubscribeMessages/unsubscribeConversation sont declares ici (portee de l'effet) et
        // non plus retournes par la fonction async fetchConversationAndMessages : un useEffect
        // ne peut nettoyer que la fonction qu'IL retourne lui-meme, pas celle d'une fonction async
        // appelee a l'interieur. Avant ce correctif, ce return interne etait ignore : les listeners
        // Firestore restaient actifs meme apres avoir quitte la page de chat, et continuaient donc
        // a marquer la conversation comme lue (readBy) a chaque nouveau message recu, meme absent.
        let unsubscribeMessages;
        let unsubscribeConversation;
        let isCancelled = false;

        const fetchConversationAndMessages = async () => {
            try {
                await messageService.markConversationAsRead(id, authState.uid);
                const { conversationRef, conversationDoc } = await chatService.getConversation(id);

                if (conversationDoc.exists()) {
                const conversationData = conversationDoc.data();
                    const otherUserId = conversationData.members.find(memberId => memberId !== authState.uid);
                    const userDetails = await chatService.fetchUserDetails(otherUserId);
                    if (isCancelled) return;
                    setOtherUser(userDetails);

                    // Subscribe to conversation changes
                    unsubscribeConversation = chatService.subscribeToConversation(
                        conversationRef,
                        (liveConversationData) => {
                            const blockedBy = liveConversationData?.blockedBy || [];
                            // Bloqué si j'ai bloqué l'autre OU si l'autre m'a bloqué : dans les deux cas
                            // l'envoi de messages doit être désactivé pour les deux côtés de la conversation.
                            setIsBlocked(blockedBy.includes(authState.uid) || blockedBy.includes(otherUserId));
                            setBlockState({ blockedBy, blockInfo: liveConversationData?.blockInfo || {} });
                        }
                    );

                    // Subscribe to messages
                    unsubscribeMessages = chatService.subscribeToMessages(
                    conversationRef,
                    authState.uid,
                    (messagesList) => {
                        if (messagesList.length > 0) {
                            setOldestMessageTimestamp(messagesList[0].createdAt);
                            setHasMoreMessages(messagesList.length === 20); // Si on reçoit exactement 20 messages, il y en a probablement plus
                        }

                        // Le filtrage des messages bloqués est recalculé de façon réactive
                        // dans displayedMessages (dépend de blockState), on stocke ici la liste brute.
                        setMessages(messagesList);
                        setLoading(false);
                        scrollToBottom();

                        // Remarque en lu à chaque nouveau message reçu pendant que la conversation
                        // est déjà ouverte (pas seulement à l'ouverture initiale de la page).
                        const lastMessage = messagesList[messagesList.length - 1];
                        if (lastMessage && !lastMessage.isUser) {
                            messageService.markConversationAsRead(id, authState.uid).catch(error => {
                                console.error('Error marking conversation as read:', error);
                            });
                        }
                        }
                );
                } else {
                    console.error('Conversation not found');
                    navigate('/messages');
                }
            } catch (error) {
                console.error('Error fetching conversation:', error);
                navigate('/messages');
            } finally {
                setLoading(false);
            }
        };

        fetchConversationAndMessages();

        // Cleanup reellement pris en compte par React (contrairement au return interne
        // de fetchConversationAndMessages) : desabonne les listeners Firestore en quittant
        // la page ou en changeant de conversation.
        return () => {
            isCancelled = true;
            if (unsubscribeMessages) unsubscribeMessages();
            if (unsubscribeConversation) unsubscribeConversation();
        };
    }, [id, authState.uid, navigate]);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!message.trim() || isBlocked) return;

        try {
            await chatService.sendMessage(id, message, authState.uid);
            setMessage('');
            scrollToBottom();
        } catch (error) {
            console.error('Error sending message:', error);
        }
    };

    const handleBlockUser = () => {
        Modal.confirm({
            title: t('chat.blockUserConfirm.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('chat.blockUserConfirm.content', { name: otherUser?.name }),
            okText: t('chat.blockUserConfirm.okText'),
            cancelText: t('chat.blockUserConfirm.cancelText'),
            onOk: async () => {
                try {
                    await chatService.blockUser(id, authState.uid);
                    setIsBlocked(true);
                    Modal.success({
                        content: t('chat.blockUserSuccess', { name: otherUser.name })
                    });
                } catch (error) {
                    console.error('Error blocking user:', error);
                    Modal.error({
                        content: t('chat.blockUserError')
                    });
                }
            }
        });
    };

    const handleUnblockUser = () => {
        Modal.confirm({
            title: t('chat.unblockUserConfirm.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('chat.unblockUserConfirm.content', { name: otherUser?.name }),
            okText: t('chat.unblockUserConfirm.okText'),
            cancelText: t('chat.unblockUserConfirm.cancelText'),
            onOk: async () => {
                try {
                    await chatService.unblockUser(id, authState.uid);
                    setIsBlocked(false);
                    Modal.success({
                        content: t('chat.unblockUserSuccess', { name: otherUser.name })
                    });
                } catch (error) {
                    console.error('Error unblocking user:', error);
                    Modal.error({
                        content: t('chat.unblockUserError')
                    });
                }
            }
        });
    };

    if (loading) {
        return <Spin size="large" fullscreen tip={t('chat.loading')} />;
    }

    return (
        <div className="page-wrapper chat-page">
            <header className="header header-fixed bg-white" ref={headerRef}>
                <div className="container">
                    <div className="header-content">
                        <div className="left-content me-3">
                            <a className="back-btn" onClick={() => navigate('/messages')}>
                                <i className="icon feather icon-arrow-left"></i>
                            </a>
                        </div>
                        <div className="mid-content d-flex align-items-center text-start">
                            <a className="media media-40 rounded-circle me-3" onClick={() => setShowProfileDetail(true)} style={{ cursor: 'pointer' }}>
                                <img src={otherUser?.avatar || '/assets/images/user/default-avatar.jpg'} alt="/" />
                            </a>
                            <div className="right-content" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                <div className="user-info">
                                   <h6 className="title">{otherUser?.name || 'Utilisateur'}</h6>
                                </div>
                                <button 
                                    className={`block-button ${isBlocked ? 'blocked' : ''}`}
                                    onClick={isBlocked ? handleUnblockUser : handleBlockUser}
                                    style={{
                                        padding: '8px 16px',
                                        borderRadius: '20px',
                                        border: 'none',
                                        backgroundColor: isBlocked ? '#ff4d4f' : '#f0f0f0',
                                        color: isBlocked ? 'white' : '#595959',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    <i className={`fa-solid ${isBlocked ? 'fa-user-check' : 'fa-user-slash'}`}></i>
                                    {/* {isBlocked ? t('chat.unblock') : t('chat.block')} */}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <div className="page-content chat space-top p-b50">
                <div className="container"> 
                    <div
                        className="chat-box-area style-2"
                        ref={chatBoxRef}
                        style={chatAreaHeight ? { height: `${chatAreaHeight}px` } : undefined}
                    >
                        {hasMoreMessages && (
                            <div className="text-center mb-3">
                                <button 
                                    className="btn btn-primary btn-sm"
                                    onClick={loadOlderMessages}
                                    disabled={isLoadingMore}
                                >
                                    {isLoadingMore ? t('chat.loading') : t('chat.loadMore')}
                                </button>
                            </div>
                        )}
                        
                        {displayedMessages.map((msg, index) => {
                            // Get previous message
                            const prevMsg = index > 0 ? displayedMessages[index - 1] : null;

                            // Check if we should show the time
                            const showTime = !prevMsg ||
                                prevMsg.senderId !== msg.senderId ||
                                moment.unix(prevMsg.createdAt).format('HH:mm') !== moment.unix(msg.createdAt).format('HH:mm');

                            return (
                                <div key={msg.id || msg._id} className={`chat-content ${msg.isUser ? 'user' : ''}`}>
                                    <div className="message-item">
                                        <div className="bubble">{msg.text}</div>
                                        {showTime && (
                                            <div className="message-time">
                                                {moment.unix(msg.createdAt).format('HH:mm')}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <footer className="footer border-top fixed bg-white" ref={footerRef}>
                <div className="container p-2">
            <div className="chat-footer">
                <form onSubmit={handleSendMessage}>
                            <div className="form-group">
                                <div className="input-wrapper message-area">
                                    <div className="append-media"></div>
                        <input
                            type="text"
                            placeholder={isBlocked ? t('chat.blockedInputPlaceholder') : t('chat.inputPlaceholder')}
                            className="form-control"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            disabled={isBlocked}
                        />
                        <button type="submit" className="btn btn-primary" disabled={isBlocked}>
                            <i className="fa-solid fa-paper-plane"></i>
                        </button>
                                </div>
                    </div>
                </form>
            </div>
                </div>
            </footer>

            {showProfileDetail && otherUser && (
                <ProfileDetail
                    user={otherUser}
                    onClose={() => setShowProfileDetail(false)}
                />
            )}
        </div>
    );
};

export default Chat;
